import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Plus, Trash2, Pencil, Loader2, Star, Save, Phone, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const emptyAddr = { label: "Rumah", recipient: "", phone: "", full_address: "", city: "", postal_code: "", notes: "", is_default: false };

export default function CustomerDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [cust, setCust] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addrDialog, setAddrDialog] = useState(false);
  const [addrForm, setAddrForm] = useState(emptyAddr);
  const [editingAddrId, setEditingAddrId] = useState(null);
  const [info, setInfo] = useState({ name: "", phone: "", email: "", notes: "" });

  const load = () => {
    api.get(`/customers/${id}`).then((r) => {
      setCust(r.data);
      setInfo({ name: r.data.name, phone: r.data.phone, email: r.data.email || "", notes: r.data.notes || "" });
    }).catch(() => toast.error("Gagal memuat data"));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const saveInfo = async () => {
    setSaving(true);
    try {
      await api.patch(`/customers/${id}`, info);
      toast.success("Data customer disimpan");
      load();
    } catch (err) {
      toast.error("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const removeCustomer = async () => {
    if (!window.confirm(`Hapus customer ${cust.name} dan semua alamatnya?`)) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer dihapus");
      nav("/customers");
    } catch (err) {
      const d = err?.response?.data?.detail || "Gagal menghapus";
      toast.error(typeof d === "string" ? d : "Gagal menghapus");
    }
  };

  const openNewAddr = () => {
    setEditingAddrId(null);
    setAddrForm({ ...emptyAddr, recipient: cust?.name || "", phone: cust?.phone || "" });
    setAddrDialog(true);
  };

  const openEditAddr = (a) => {
    setEditingAddrId(a.id);
    setAddrForm({ ...a });
    setAddrDialog(true);
  };

  const saveAddr = async () => {
    if (!addrForm.full_address) {
      toast.error("Alamat lengkap wajib diisi");
      return;
    }
    try {
      if (editingAddrId) {
        await api.patch(`/customers/${id}/addresses/${editingAddrId}`, addrForm);
        toast.success("Alamat diperbarui");
      } else {
        await api.post(`/customers/${id}/addresses`, addrForm);
        toast.success("Alamat ditambahkan");
      }
      setAddrDialog(false);
      load();
    } catch (err) {
      toast.error("Gagal menyimpan alamat");
    }
  };

  const removeAddr = async (addrId) => {
    if (!window.confirm("Hapus alamat ini?")) return;
    try {
      await api.delete(`/customers/${id}/addresses/${addrId}`);
      toast.success("Alamat dihapus");
      load();
    } catch (err) {
      toast.error("Gagal menghapus");
    }
  };

  const setDefault = async (a) => {
    try {
      await api.patch(`/customers/${id}/addresses/${a.id}`, { ...a, is_default: true });
      load();
    } catch {
      toast.error("Gagal");
    }
  };

  if (!cust) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="customer-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="gap-1">
          <ArrowLeft className="w-4 h-4" /> Kembali
        </Button>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-slate-900 text-white font-bold flex items-center justify-center text-2xl">
            {cust.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{cust.name}</h1>
            <div className="text-sm text-slate-500 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {cust.phone}</span>
              {cust.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {cust.email}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/intake?customer=${cust.id}`}>
            <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="new-service-for-customer">
              <Plus className="w-4 h-4" /> Order Servis Baru
            </Button>
          </Link>
          {can.deleteCustomer(user?.role) && (
            <Button variant="outline" onClick={removeCustomer} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-customer-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <h3 className="font-display font-semibold text-slate-900 mb-3">Data Kontak</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Nama Pelanggan</Label>
            <Input value={info.name} onChange={(e) => setInfo((f) => ({ ...f, name: e.target.value }))} data-testid="info-name" />
          </div>
          <div>
            <Label>No. HP</Label>
            <Input value={info.phone} onChange={(e) => setInfo((f) => ({ ...f, phone: e.target.value }))} data-testid="info-phone" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={info.email} onChange={(e) => setInfo((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="sm:col-span-2">
            <Label>Catatan</Label>
            <Textarea rows={2} value={info.notes} onChange={(e) => setInfo((f) => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
        <div className="mt-3">
          <Button onClick={saveInfo} disabled={saving} className="bg-slate-900 gap-2" data-testid="info-save-btn">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </div>
      </div>

      {/* Addresses */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" /> Alamat Pengiriman ({cust.addresses?.length || 0})
          </h3>
          <Button size="sm" variant="outline" onClick={openNewAddr} className="gap-1" data-testid="add-address-btn">
            <Plus className="w-3.5 h-3.5" /> Tambah Alamat
          </Button>
        </div>
        {(!cust.addresses || cust.addresses.length === 0) ? (
          <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center text-sm text-slate-500">
            Belum ada alamat pengiriman. Klik "Tambah Alamat" untuk menambah.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {cust.addresses.map((a) => (
              <div key={a.id} data-testid={`address-card-${a.id}`} className="border border-slate-200 rounded-lg p-3 relative">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                        {a.label}
                      </span>
                      {a.is_default ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Default
                        </span>
                      ) : (
                        <button onClick={() => setDefault(a)} className="text-[10px] text-slate-400 hover:text-slate-700 underline">
                          Jadikan default
                        </button>
                      )}
                    </div>
                    <div className="text-sm text-slate-800 mt-1 whitespace-pre-line">{a.full_address}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {[a.city, a.postal_code].filter(Boolean).join(" · ")}
                    </div>
                    {a.recipient && (
                      <div className="text-xs text-slate-600 mt-1 pt-1 border-t border-slate-100">
                        Penerima: {a.recipient}{a.phone ? ` · ${a.phone}` : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditAddr(a)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50 hover:text-red-700" onClick={() => removeAddr(a.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={addrDialog} onOpenChange={setAddrDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAddrId ? "Edit Alamat" : "Tambah Alamat"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Label</Label>
                <Input value={addrForm.label} onChange={(e) => setAddrForm((f) => ({ ...f, label: e.target.value }))} placeholder="Rumah / Kantor / Toko" data-testid="addr-label" />
              </div>
              <div>
                <Label>Kode Pos</Label>
                <Input value={addrForm.postal_code} onChange={(e) => setAddrForm((f) => ({ ...f, postal_code: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nama Penerima</Label>
                <Input value={addrForm.recipient} onChange={(e) => setAddrForm((f) => ({ ...f, recipient: e.target.value }))} />
              </div>
              <div>
                <Label>No. HP Penerima</Label>
                <Input value={addrForm.phone} onChange={(e) => setAddrForm((f) => ({ ...f, phone: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Kota</Label>
              <Input value={addrForm.city} onChange={(e) => setAddrForm((f) => ({ ...f, city: e.target.value }))} />
            </div>
            <div>
              <Label>Alamat Lengkap *</Label>
              <Textarea rows={3} value={addrForm.full_address} onChange={(e) => setAddrForm((f) => ({ ...f, full_address: e.target.value }))} placeholder="Jalan, blok, nomor, RT/RW, kelurahan, kecamatan" data-testid="addr-full" />
            </div>
            <div>
              <Label>Catatan Tambahan</Label>
              <Input value={addrForm.notes} onChange={(e) => setAddrForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Patokan, warna pagar, dll." />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="isdefault" checked={addrForm.is_default} onChange={(e) => setAddrForm((f) => ({ ...f, is_default: e.target.checked }))} />
              <label htmlFor="isdefault" className="text-sm text-slate-700">Jadikan alamat default</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddrDialog(false)}>Batal</Button>
            <Button onClick={saveAddr} className="bg-slate-900" data-testid="addr-save">{editingAddrId ? "Simpan" : "Tambah"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
